/** @format */

import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from '@material-ui/core';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import I18n from '../../../components/Translate/I18n';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import CustomInputExt from '../../../components/CustomInput/CustomInputExt';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ApnNameInterface, ApnNamePutInterface } from '../../../interfaces/collections/ApnNameInterface';
import { NotificationHelper } from '../../../helpers/NotificationHelper';

type PlatformResellerEditApnNameType = {
    open: boolean;
    apnName: ApnNameInterface;
    reload(): void;
    closed(): void;
};

export const PlatformResellerEditApnName: FC<PlatformResellerEditApnNameType> = ({ open, apnName, reload, closed }): ReactElement => {
    const dispatch = useDispatch();
    const [apnNameEdit, setApnNameEdit] = useState<string | null>(apnName.apnName);
    const [apnNameId, setApnNameId] = useState<number | null>(apnName.id);
    const [enableWithoutIp, setEnableWithoutIp] = useState(apnName.enableWithoutIp);

    useEffect(() => {
        if (apnNameId !== apnName.id) {
            setApnNameEdit(apnName.apnName);
            setApnNameId(apnName.id);
            setEnableWithoutIp(apnName.enableWithoutIp);
        }
    }, [apnName, apnNameId]);

    const save = (): void => {
        if (apnNameEdit === null || apnNameEdit.length === 0) {
            NotificationHelper.notifyError('pools:apn.empty_name', dispatch);
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(apnName['@id']),
            'add-new-apn-name',
            {
                apnName: apnNameEdit,
                isDeleted: apnName.isDeleted,
                enableWithoutIp: enableWithoutIp,
            } as ApnNamePutInterface,
            {
                onSuccess: (data: ApnNameInterface): void => {
                    NotificationHelper.notifySaveSuccess(dispatch);
                    reload();
                    closed();
                },
                onError: (data: {
                    message: {
                        message?: string | null;
                    };
                }): void => {
                    if (data.message && data.message.message) {
                        NotificationHelper.notifyError('pools:apn.' + data.message.message, dispatch);
                    }
                },
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    };

    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        const checked = event.target.checked;
        setEnableWithoutIp(checked);
    };

    return (
        <Dialog open={open} onClose={closed} maxWidth={'sm'} fullWidth={true}>
            <DialogTitle>
                <I18n trans={apnName.isPublic ? 'pools:apn.edit_apn_name_public' : 'pools:apn.edit_apn_name_private'} />
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={12}>
                        <CustomInputExt
                            fullWidth={true}
                            labelText="pools:apn.apn_name"
                            onChange={(event: React.FormEvent<HTMLInputElement>): void => {
                                setApnNameEdit(event.currentTarget.value);
                            }}
                            value={apnNameEdit}
                        />
                    </GridItem>
                    <GridItem xs={6} key={'apn_enable_without_ip'}>
                        <FormControlLabel
                            control={<Checkbox checked={enableWithoutIp} name={''} onChange={onCheckBoxEvent} />}
                            label={<I18n trans={'pools:apn.enable_without_ip'} />}
                        />
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={closed} callbackParam={{}} size={'sm'} label="general:common.cancel" />
                <CustomTextButtom callback={save} callbackParam={{}} size={'sm'} label="general:common.save" />
            </DialogActions>
        </Dialog>
    );
};
